import { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ADDRESS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { languages } from 'utils/localization';

import useAlert from './useAlert';
import useAxiosPrivate from './useAxiosPrivate';
import useLocale from './useLocale';

const useGoogleAddress = ({
	name,
	restrictions,
	showCountries,
	setValue,
	onAddressChange,
	onPlaceChange,
	watchedAddress,
	useGeocodeParse = true,
	selectedCountry,
	isGetLatLng,
	preserveTextValue,
	restrictCountrySearch,
	variantForAddressGroup
}) => {
	const maps = window?.google?.maps;

	const axios = useAxiosPrivate();
	const { showError } = useAlert();
	const { t } = useTranslation();
	const formMethods = useFormContext();
	const { LanguageCode } = useLocale();

	const [predictions, setPredictions] = useState([]);
	const [isLoading, setIsLoading] = useState(false);

	const language = useMemo(
		() => languages.find((l) => l.code === LanguageCode)?.languageCode ?? 'en',
		[LanguageCode]
	);

	const options = useMemo(() => {
		const requestObj = {
			fields: ['formatted_address', 'geometry', 'place_id', 'types', 'address_components'],
			types: showCountries
				? ['establishment', 'geocode']
				: ['administrative_area_level_2', 'locality', 'administrative_area_level_3'],
			language
		};

		// add the componentRestrictions to the options object
		if (restrictions) {
			requestObj.componentRestrictions = {};
			Object.entries(restrictions).forEach(([key, restriction]) => {
				if (restriction?.code) {
					requestObj.componentRestrictions[key] = restriction?.code;
				}
			});
		}

		// added the country to the componentRestrictions for only selected country results
		if (selectedCountry && restrictCountrySearch) {
			requestObj.componentRestrictions = {
				...requestObj.componentRestrictions,
				country: selectedCountry
			};
		}

		return requestObj;
	}, [restrictions, showCountries, language, selectedCountry, restrictCountrySearch]);

	// set value of the address object from after backend parses the address
	const handleValueChange = useCallback(
		(data) => {
			if (!data) return;
			if (!setValue) return;

			const lat = data?.lat ? (typeof data.lat === 'function' ? data.lat() : data.lat) : null;
			const lng = data?.lng ? (typeof data.lng === 'function' ? data.lng() : data.lng) : null;
			setValue(
				name,
				{
					...watchedAddress,
					// text
					...(preserveTextValue ? {} : { text: data?.text || null }),
					// country
					countryName: data?.countryShortCode || null,
					countryID: null,
					// state
					stateName: data?.stateName || null,
					// city
					cityName: data?.cityName || null,
					cityID: null,
					// district
					districtName: data?.districtName || null,
					// neighbourhood
					neighbourhoodName: data?.neighbourhoodName || null,
					// street
					street: data?.streetName || null,
					// building
					buildingName: data?.buildingName || null,
					// zipCode
					zipCode: data?.zipCode || null,
					// shortAddressCode
					shortAddressCode: data?.shortAddressCode || null,
					// lat, lng
					lat,
					lng
				},
				{
					shouldValidate: true,
					shouldDirty: true
				}
			);
		},
		[watchedAddress]
	);

	// get place details from google geocode service by lat, lng
	const getPlaceByGeocode = async (lat, lng) => {
		if (!lat || !lng) return;

		const latlng = new maps.LatLng(lat, lng);

		const { Geocoder } = await maps.importLibrary('geocoding');
		const geocoder = new Geocoder();
		return await geocoder
			.geocode({
				...options,
				location: latlng
			})
			.then(({ results }) => {
				if (results?.length > 1) {
					const place = results?.find((r) => !r.types.includes('plus_code'));

					return place;
				} else {
					return results[0];
				}
			})
			.catch(() => {
				AppLogger.error(
					"AddressInput: Can't find location geocoder.geocode on getPlaceByGeocoder condition => ",
					lat,
					lng
				);
				showError({
					message: t('errorMsg.addressNotFoundTryManually')
				});
				return null;
			});
	};

	// parse address by geocodeParse service by place object
	const getAddressFromService = async (place, latLng, text) => {
		setIsLoading(true);
		await axios
			.post(ADDRESS.GOOGLE_GEOCODE_PARSE, {
				results: [place]
			})
			.then((res) => {
				const { data = {} } = res;
				onAddressChange?.({
					...data,
					...(isGetLatLng ? { latLng: { lat: latLng?.lat(), lng: latLng?.lng() } } : null)
				});
				handleValueChange({
					text,
					lat: latLng?.lat,
					lng: latLng?.lng,
					...data
				});
			})
			.catch((error) => {
				handleValueChange({
					text,
					lat: latLng?.lat,
					lng: latLng?.lng
				});
				AppLogger.error(error);
			});
		setIsLoading(false);
	};

	// parse address by geocodeParse service by place object and get some data from google maps
	const parseAddress = async (place, selectedAddress) => {
		if (!place) return;
		const placeData = Array.isArray(place) ? place[0] : place;

		if (placeData?.geometry) {
			const { geometry } = placeData;
			const [lat, lng] = [geometry.location.lat, geometry.location.lng];

			const addressText = selectedAddress || placeData?.formatted_address || '';

			useGeocodeParse && (await getAddressFromService(place, { lat, lng }, addressText));
		} else if (placeData?.name) {
			const [lat, lng] =
				placeData?.name.split(' ').length > 1 ? placeData.name.split(' ') : placeData.name.split(',');

			if (!lat || !lng) return;

			if (isNaN(Number(lat)) || isNaN(Number(lng))) return;

			const place = await getPlaceByGeocode(lat, lng);

			if (!place) return;

			const text = place?.formatted_address || '';

			useGeocodeParse && (await getAddressFromService(place, { lat, lng }, text));
		} else {
			AppLogger.error(
				"AddressInput: Can't find placeData.geometry or placeData.name on parseAddress condition => ",
				placeData
			);
		}
	};

	// get place details from google maps by place_id
	const getPlaceDetails = async (selection) => {
		if (!selection?.place_id) return;

		const placeId = selection.place_id;

		const request = {
			placeId,
			language: 'en'
		};

		try {
			const { Geocoder } = await maps.importLibrary('geocoding');
			const geocoder = new Geocoder();
			await geocoder.geocode(request, async (places, status) => {
				const place = places[0];
				if (!place) return;

				if (status === 'OK') {
					await parseAddress(place, selection?.description);
					onPlaceChange && (await onPlaceChange(place, selection?.description));
				}
			});
		} catch (error) {
			AppLogger.error(error);
		}
	};

	// search predictions by given term on google maps
	const searchPredictions = async (searchTerm) => {
		if (!searchTerm) return;

		const result = [false, null];

		try {
			const { AutocompleteService } = await maps.importLibrary('places');
			const autocompleteService = new AutocompleteService();

			await autocompleteService.getPlacePredictions(
				{
					...options,
					input: searchTerm
				},
				async (predictions, status) => {
					if (status === 'OK') {
						result[0] = true;
						result[1] = predictions;
						return result;
					} else {
						if (!predictions?.length) {
							// check if user entered lat, lng
							const [lat, lng] =
								searchTerm.split(' ').length === 2
									? searchTerm.split(' ')
									: searchTerm.split(',').length === 2
										? searchTerm.split(',')
										: [null, null];
							if (!lat || !lng) return result;

							if (!isNaN(Number(lat)) || !isNaN(Number(lng))) {
								// lat, lng from google maps
								const place = await getPlaceByGeocode(lat, lng);

								if (!place) return result;

								onPlaceChange && (await onPlaceChange(place, null));

								await parseAddress(place);
							} else {
								return result;
							}
						} else {
							result[0] = true;
							result[1] = predictions;
							return result;
						}
					}
				}
			);
		} catch (error) {
			AppLogger.error(error);
			return result;
		}

		return result;
	};

	// handle input change
	const handleInputChange = async (value) => {
		if (!value) {
			setPredictions([]);
			return;
		}
		if (variantForAddressGroup === 'shortNationalAddress' && selectedCountry === 'SA') {
			await getSPLShortAddress(value);
		} else {
			const [status, googlePredictions] = await searchPredictions(value);
			if (!status) return;

			setPredictions(googlePredictions);
		}
	};

	const getSPLShortAddress = async (value) => {
		try {
			if (value.length < 8) {
				formMethods?.setError?.(`${name}.text`, {
					message: t('validation.numberFieldLength', {
						length: 8,
						fieldName: t('shortNationalAddress')
					})
				});
				return;
			}
			if (value.length > 8) {
				return;
			}
			setIsLoading(true);
			const response = await axios.post(ADDRESS.GET_ADDRESS, {
				shortAddressCode: value
			});
			const { status, data } = response;

			if (status === 200 && Object.keys(data).length > 0) {
				onAddressChange?.(data);
				handleValueChange({
					...data,
					text: `${value}, ${data.streetName}, ${data.cityName}, ${data.countryName}`,
					shortAddressCode: value
				});
			} else {
				AppLogger.error(response);
				showError({
					message: t('errorMsg.addressNotFoundTryManually')
				});
			}
		} catch (error) {
			showError({
				message: t('errorMsg.addressNotFoundTryManually')
			});
			AppLogger.error(error);
		} finally {
			setIsLoading(false);
		}
	};

	const getPlaceDataOnFirstLoad = async (searchTerm) => {
		if (!searchTerm) return;
		const [status, googlePredictions] = await searchPredictions(searchTerm);
		if (!status) return;

		const place = googlePredictions[0];

		const placeId = place?.place_id;
		if (!placeId) return;

		await getPlaceDetails(place);
	};

	return {
		predictions,
		setPredictions,
		isLoading,
		setIsLoading,
		handleInputChange,
		getPlaceDataOnFirstLoad,
		getPlaceDetails,
		parseAddress,
		handleValueChange
	};
};

export default useGoogleAddress;
